import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { Animated } from 'react-native'

import { Message, type TMessageProps } from '@/src/elements/message'
import { useSyncedRef } from '@/src/general/hooks/useSyncedRef'

const ZINDEX_OVER_ALL_OTHER_ELEMENTS = 50

export type TToastProps = TMessageProps & {
  id: string
  message: string
  removeToast: (id: string) => void
}

export const Toast = (props: TToastProps): ReactNode => {
  const { message, removeToast, id, buttons: button, ...otherToastProps } = props
  // useAnimatedValue is not supported by rn-web yet, see : https://github.com/necolas/react-native-web/pull/2740
  const animValue = useMemo(() => new Animated.Value(200), [])
  const removeToastRef = useSyncedRef(removeToast)

  // If there is a button, the toast will stay for 15 seconds instead of 4 seconds
  // so that the user has time to click on the button
  const delay = button ? 15000 : 4000

  const hideToast = useCallback(() => {
    Animated.spring(animValue, {
      toValue: 200,
      useNativeDriver: true,
    }).start(() => {
      removeToastRef.current?.(id)
    })
  }, [removeToastRef, id, animValue])

  useEffect(() => {
    Animated.spring(animValue, {
      toValue: 0,
      useNativeDriver: true,
    }).start()

    const timer = setTimeout(() => {
      hideToast()
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [animValue, delay, hideToast])

  return (
    <Animated.View
      style={{
        zIndex: ZINDEX_OVER_ALL_OTHER_ELEMENTS,
        position: 'absolute',
        bottom: 0,
        minWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        paddingHorizontal: 16,
        transform: [{ translateY: animValue }],
      }}
    >
      <Message
        {...otherToastProps}
        buttons={button}
        onClose={hideToast}
        className="m-4 max-w-[500px] shadow-xl"
        textClassName="items-center"
      >
        {message}
      </Message>
    </Animated.View>
  )
}
